import React from "react";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import {
    SectionTitle,
    StyledTextField,
    PointsTextField,
    EventsListBox,
    EventItemBox,
    StyledAddEventButton,
} from "@components/views/editor/gameEditor/GameEditor.styles";
import { Box } from "@mui/material";
import { GameEvent } from "@components/views/editor/gameEditor/models/GameEditorData";
import { isValidEventKey } from "@constants/eventValidation";

interface EventsListProps {
    events: GameEvent[];
    maxPoints: number;
    onChange: (events: GameEvent[]) => void;
}

export const EventsList = ({ events, maxPoints, onChange }: EventsListProps) => {
    const { t } = useTranslation();

    const handleEventChange = (index: number, value: string) => {
        const newEvents = [...events];
        newEvents[index] = { ...newEvents[index], event: value };
        onChange(newEvents);
    };

    const handlePointsChange = (index: number, value: number) => {
        const clamped = Math.max(0, Math.min(maxPoints, value));
        const newEvents = [...events];
        newEvents[index] = { ...newEvents[index], points: clamped };
        onChange(newEvents);
    };

    const handleAddEvent = () => {
        onChange([...events, { event: "", points: 0 }]);
    };

    const handleRemoveEvent = (index: number) => {
        const newEvents = events.filter((_, i) => i !== index);
        onChange(newEvents);
    };

    return (
        <Box>
            <SectionTitle>{t("gameEditor.events")}</SectionTitle>
            <EventsListBox>
                {events.map((item, index) => (
                    <EventItemBox key={index}>
                        <StyledTextField
                            fullWidth
                            size="small"
                            placeholder={t("gameEditor.eventPlaceholder")}
                            value={item.event}
                            onChange={(e) => handleEventChange(index, e.target.value)}
                            error={item.event.trim() !== "" && !isValidEventKey(item.event)}
                            helperText={
                                item.event.trim() !== "" && !isValidEventKey(item.event)
                                    ? t("gameEditor.validation.eventFormat")
                                    : t("gameEditor.validation.eventFormatExample")
                            }
                        />
                        <PointsTextField
                            size="small"
                            type="number"
                            label={t("gameEditor.points")}
                            value={item.points}
                            onChange={(e) => handlePointsChange(index, parseInt(e.target.value, 10) || 0)}
                            inputProps={{ min: 0, max: maxPoints }}
                            InputLabelProps={{ shrink: true }}
                        />
                        <IconButton
                            size="small"
                            onClick={() => handleRemoveEvent(index)}
                            color="error"
                            aria-label={t("gameEditor.removeEvent")}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </EventItemBox>
                ))}
            </EventsListBox>
            <StyledAddEventButton
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddEvent}
                size="small"
            >
                {t("gameEditor.addEvent")}
            </StyledAddEventButton>
        </Box>
    );
};
